/**
 * 后台服务模块
 * 职责：全局监听 PLC 信号，自动完成配方下发与报表记录
 * 功能：
 *   1. 配方数据写入 PLC：监测 DB401_DBW0 bit0，轮询 M_TRANSFER 队列发送配方
 *   2. 记录报表：实时监测 PLC 信号，自动记录称量数据（R_WEIGH）和压力曲线（R_CURVE）
 * 生命周期：由 index.ts 在应用启动时初始化，与页面无关
 *
 * 状态机：IDLE → CHECKING → SENDING → GUARDING/WAITING → IDLE
 *   - 一个 PLC 请求周期（DB401=1 → DB401=0）最多只发一条配方
 *   - WAITING 期间发现新配方立即补发（同周期第一条）
 */

import { Plc } from "./scada";
import { tags } from "./tags";

const plc = new Plc(tags, false, { silent: true, noDOM: true });

declare const mini: any;

// ========== 状态机 ==========

type State = 'IDLE' | 'CHECKING' | 'SENDING' | 'GUARDING' | 'WAITING';

let state: State = 'IDLE';
let waitTimer: any = null;
let isRecordingWeigh = [false, false, false]; // 1#2#3# 是否正在记录中

// ========== 跨 iframe 获取主监控页面 plc 实例 ==========

/**
 * 遍历顶层 window 的所有 iframe，找到主监控页面（P_MAIN_VIEW）的 plc 实例。
 * background.ts 在顶层 window 运行，P_MAIN_VIEW 在 iframe 中运行，两者 JavaScript 上下文隔离，
 * window.plc 不是同一个对象，必须通过 iframe.contentWindow 访问。
 */
function getMonitorPlc(): any {
    const topWin = window.top || window;
    const iframes = topWin.document.querySelectorAll('iframe');
    for (let i = 0; i < iframes.length; i++) {
        const iframe = iframes[i] as HTMLIFrameElement;
        try {
            const cw = iframe.contentWindow;
            if (cw && cw.location && cw.location.href && cw.location.href.includes('P_MAIN_VIEW')) {
                return (cw as any).plc;
            }
        } catch (e) {
            // 跨域 iframe 会抛异常，跳过
        }
    }
    return null;
}

// ========== 压力曲线记录器（方案 B：内存缓存 + 20s 批量写入） ==========

interface CurvePoint {
    batch: number;
    maxPressure: number;
    feedPressure: number;
    time: string;
}

const curveRecorders = [
    { buffer: [] as CurvePoint[], readTimer: null as any, flushTimer: null as any, isRunning: false }, // 1#
    { buffer: [] as CurvePoint[], readTimer: null as any, flushTimer: null as any, isRunning: false }, // 2#
    { buffer: [] as CurvePoint[], readTimer: null as any, flushTimer: null as any, isRunning: false }, // 3#
];

/** 各系统 PLC Tag 映射（bit 位置、输送次数、加压压力、输送压力） */
const curveTagMap = [
    { bit: 3, batchField: 'DB401_DBD14', maxField: 'DB112_DBD4',  feedField: 'DB112_DBD8' },  // 1#
    { bit: 5, batchField: 'DB401_DBD22', maxField: null,         feedField: 'DB212_DBD4' },  // 2# 无加压压力
    { bit: 7, batchField: 'DB401_DBD30', maxField: 'DB312_DBD4',  feedField: 'DB312_DBD8' },  // 3#
];

function formatCurveTime(d: Date): string {
    const pad = (n: number) => n < 10 ? '0' + n : '' + n;
    return d.getFullYear() + pad(d.getMonth() + 1) + pad(d.getDate())
        + pad(d.getHours()) + pad(d.getMinutes()) + pad(d.getSeconds());
}

// ========== PLC 连接 ==========

export function startBackgroundService() {
    let lastDbw0 = 0;

    plc.on('open', (e: any) => {
        console.log('[Background] PLC connected:', e.equipmentName, e.equipmentAddr);
        // 初始检查压力曲线信号（防止启动时输送已开始而漏记）
        const initialValue = (plc as any).DB401_DBW0 || 0;
        lastDbw0 = initialValue;
        checkCurveSignal(1, initialValue);
        checkCurveSignal(2, initialValue);
        checkCurveSignal(3, initialValue);
    });

    plc.on('error', (e: any) => {
        console.error('[Background] PLC error:', e.code, e.msg);
    });

    // 监听 DB401_DBW0 变化
    (plc as any).on('DB401_DBW0', (e: any) => {
        const value = e.tagValue || 0;

        // bit0: 配方发送请求（四状态机）
        const bit0 = (value & 1) ? 1 : 0;
        if (bit0 === 1 && state === 'IDLE') {
            state = 'CHECKING';
            checkAndSend();
        }
        else if (bit0 === 0 && (state === 'GUARDING' || state === 'WAITING')) {
            state = 'IDLE';
            stopWaitTimer();
        }

        // bit2: 1#称量完成
        if (value & 4) {
            recordWeighData(1);
        }

        // bit4: 2#称量完成
        if (value & 16) {
            recordWeighData(2);
        }

        // bit6: 3#称量完成
        if (value & 64) {
            recordWeighData(3);
        }

        // bit3/bit5/bit7: 1#2#3# 输送开始（压力曲线记录）
        checkCurveSignal(1, value);
        checkCurveSignal(2, value);
        checkCurveSignal(3, value);

        // 检测 bit3/bit5/bit7 下降沿，记录输送完成批次到 M_PLAN 和 R_TRANSFER
        const lastBit3 = (lastDbw0 >> 3) & 1;
        const lastBit5 = (lastDbw0 >> 5) & 1;
        const lastBit7 = (lastDbw0 >> 7) & 1;
        const bit3 = (value >> 3) & 1;
        const bit5 = (value >> 5) & 1;
        const bit7 = (value >> 7) & 1;

        if (lastBit3 === 1 && bit3 === 0) {
            recordBatchFinish(1);
        }
        if (lastBit5 === 1 && bit5 === 0) {
            recordBatchFinish(2);
        }
        if (lastBit7 === 1 && bit7 === 0) {
            recordBatchFinish(3);
        }

        lastDbw0 = value;
    });

    // 页面刷新/关闭前兜底：flush 缓存中的压力曲线数据
    if (typeof window !== 'undefined') {
        window.addEventListener('beforeunload', () => {
            [1, 2, 3].forEach(sysNo => {
                if (curveRecorders[sysNo - 1].isRunning) {
                    stopCurveRecording(sysNo);
                }
            });
        });
    }

    plc.open();

    // 启动实时数据定时上报（每秒写入 M_REALTIME_DATA，供微信小程序使用）
    setInterval(() => {
        const dbw0 = (plc as any).DB401_DBW0 || 0;
        jQuery.ajax({
            url: './php/AjaxService_CM.php?method=updateRealtimeData',
            type: 'post',
            data: {
                BATCH1_WEIGH_FINISH: (plc as any).DB401_DBD10 || 0,
                BATCH2_WEIGH_FINISH: (plc as any).DB401_DBD18 || 0,
                BATCH3_WEIGH_FINISH: (plc as any).DB401_DBD26 || 0,
                BATCH1_FINISH: (plc as any).DB401_DBD14 || 0,
                BATCH2_FINISH: (plc as any).DB401_DBD22 || 0,
                BATCH3_FINISH: (plc as any).DB401_DBD30 || 0,
                WEIGHT1: (plc as any).DB112_DBD20 || 0,
                WEIGHT2: (plc as any).DB212_DBD8 || 0,
                WEIGHT3: (plc as any).DB312_DBD20 || 0,
                FEED_START_1: (dbw0 & 1) ? 1 : 0,
                FEED_START_2: (dbw0 & 2) ? 1 : 0,
                FEED_START_3: (dbw0 & 4) ? 1 : 0,
                MAX_PRESSURE_1: (plc as any).DB112_DBD4 || 0,
                FEED_PRESSURE_1: (plc as any).DB112_DBD8 || 0,
                FEED_PRESSURE_2: (plc as any).DB212_DBD4 || 0,
                MAX_PRESSURE_3: (plc as any).DB312_DBD4 || 0,
                FEED_PRESSURE_3: (plc as any).DB312_DBD8 || 0,
                SAVE_TIME: formatCurveTime(new Date())
            },
            error: () => {
                console.error('[Background] updateRealtimeData 上报失败');
            }
        });
    }, 1000);
}

// ========== 状态转换 ==========

/**
 * CHECKING：查询 M_TRANSFER，决定进入 SENDING 还是 WAITING
 */
function checkAndSend() {
    queryTransferQueue((row: any | null) => {
        if (row) {
            state = 'SENDING';
            doSendRecipe(row, () => {
                // 发送完成后，根据当前 DB401 状态决定下一步
                const bit0 = (plc as any).DB401_DBW0 & 1;
                state = (bit0 === 1) ? 'GUARDING' : 'IDLE';
            });
        } else {
            // 队列为空，进入 WAITING 轮询
            state = 'WAITING';
            startWaitTimer();
        }
    });
}

/**
 * 记录称量数据
 * @param sysNo 系统编号 1/2/3
 */
function recordWeighData(sysNo: number) {
    const idx = sysNo - 1;
    if (isRecordingWeigh[idx]) return; // 正在记录中，忽略重复触发
    isRecordingWeigh[idx] = true;

    const batchField = sysNo === 1 ? 'DB401_DBD10' : sysNo === 2 ? 'DB401_DBD18' : 'DB401_DBD26';
    const weightField = sysNo === 1 ? 'DB401_DBD90' : sysNo === 2 ? 'DB401_DBD94' : 'DB401_DBD98';
    const bitField = sysNo === 1 ? 'DB401_DBW0_bit2' : sysNo === 2 ? 'DB401_DBW0_bit4' : 'DB401_DBW0_bit6';

    const batch = (plc as any)[batchField] || 0;
    const weight = (plc as any)[weightField] || 0;

    console.log(`[Background] ${sysNo}#称量完成: 批次=${batch}, 重量=${weight}`);

    jQuery.ajax({
        url: './php/AjaxService_CM.php?method=recordWeigh',
        data: {
            SYS_NO: sysNo,
            BATCH: batch,
            WEIGHT: weight
        },
        type: 'post',
        success: (text: string) => {
            const result = mini.decode(text);
            if (result.success) {
                console.log(`[Background] ${sysNo}#称量记录成功`);
                // 复位称量完成信号
                try {
                    (plc as any)[bitField] = 0;
                } catch (e) {
                    console.error(`[Background] ${sysNo}#称量信号复位失败`, e);
                }
            } else {
                console.error(`[Background] ${sysNo}#称量记录失败:`, result.message);
            }
        },
        error: () => {
            console.error(`[Background] ${sysNo}#称量记录请求失败`);
        },
        complete: () => {
            isRecordingWeigh[idx] = false;
        }
    });
}

/**
 * 输送完成下降沿：更新 M_PLAN 和 R_TRANSFER 的批次完成数
 */
function recordBatchFinish(sysNo: number) {
    const batchField = sysNo === 1 ? 'DB401_DBD14' : sysNo === 2 ? 'DB401_DBD22' : 'DB401_DBD130';
    const batch = (plc as any)[batchField] || 0;

    console.log(`[Background] ${sysNo}#输送完成下降沿: 批次=${batch}`);

    jQuery.ajax({
        url: './php/AjaxService_CM.php?method=recordBatchFinish',
        data: {
            SYS_NO: sysNo,
            BATCH: batch
        },
        type: 'post',
        success: (text: string) => {
            const result = mini.decode(text);
            if (result.success) {
                console.log(`[Background] ${sysNo}#输送完成记录成功`);
            } else {
                console.error(`[Background] ${sysNo}#输送完成记录失败:`, result.message);
            }
        },
        error: () => {
            console.error(`[Background] ${sysNo}#输送完成记录请求失败`);
        }
    });
}

/**
 * WAITING：定时轮询，等待新配方下达
 */
function startWaitTimer() {
    if (waitTimer) return;
    waitTimer = setInterval(() => {
        if (state !== 'WAITING') return;

        const bit0 = (plc as any).DB401_DBW0 & 1;
        if (bit0 === 0) {
            // PLC 已复位，结束等待
            state = 'IDLE';
            stopWaitTimer();
            return;
        }

        // DB401 仍为 1，检查队列是否有新记录
        queryTransferQueue((row: any | null) => {
            if (row && state === 'WAITING') {
                state = 'SENDING';
                doSendRecipe(row, () => {
                    // 发送完成后，DB401 仍为 1 → GUARDING（本周期已发过）
                    state = 'GUARDING';
                });
            }
        });
    }, 500);
}

function stopWaitTimer() {
    if (waitTimer) {
        clearInterval(waitTimer);
        waitTimer = null;
    }
}

// ========== 数据操作 ==========

/**
 * 查询 M_TRANSFER 最早记录
 */
function queryTransferQueue(callback: (row: any | null) => void) {
    jQuery.ajax({
        url: './php/AjaxService_CM.php?method=getTransferQueue',
        type: 'get',
        success: (text: string) => {
            const result = mini.decode(text);
            callback(result.success && result.data ? result.data : null);
        },
        error: () => {
            callback(null);
        }
    });
}

/**
 * 发送配方到 PLC 并记录历史
 */
function doSendRecipe(row: any, callback: () => void) {
    const taskId = parseInt((row.LOT_ID || '').slice(-4), 10);
    const batch = parseInt(row.BATCH) || 0;

    // 1. 写入 PLC（DB401）
    try {
        (plc as any).DB401_DBD2 = taskId;
        (plc as any).DB401_DBD6 = batch;
        (plc as any).DB401_DBD66 = parseFloat(row.WEIGHT1_SET) || 0;
        (plc as any).DB401_DBD70 = parseFloat(row.TOLERANCE1_SET) || 0;
        (plc as any).DB401_DBD74 = parseFloat(row.WEIGHT2_SET) || 0;
        (plc as any).DB401_DBD78 = parseFloat(row.TOLERANCE2_SET) || 0;
        (plc as any).DB401_DBD82 = parseFloat(row.WEIGHT3_SET) || 0;
        (plc as any).DB401_DBD86 = parseFloat(row.TOLERANCE3_SET) || 0;

                // 同时写入本地内存标签，供主监控界面模板绑定显示
        // background.ts 在顶层 window 运行，P_MAIN_VIEW 在 iframe 中运行，两者 window 隔离
        // 通过遍历 iframe 找到主监控页面的 plc 实例直接写入
        const monitorPlc = getMonitorPlc();
        if (monitorPlc) {
            monitorPlc.recipeName = row.RECIPE_NAME || '';
            monitorPlc.lotID = row.LOT_ID || '';
            monitorPlc.batchSet = parseInt(row.BATCH, 10) || 0;
            monitorPlc.weight1Set = parseFloat(row.WEIGHT1_SET) || 0;
            monitorPlc.tolerance1Set = parseFloat(row.TOLERANCE1_SET) || 0;
            monitorPlc.weight2Set = parseFloat(row.WEIGHT2_SET) || 0;
            monitorPlc.tolerance2Set = parseFloat(row.TOLERANCE2_SET) || 0;
            monitorPlc.weight3Set = parseFloat(row.WEIGHT3_SET) || 0;
            monitorPlc.tolerance3Set = parseFloat(row.TOLERANCE3_SET) || 0;
            console.log('[Background] 已同步配方信息到主监控界面 plc 内存标签');
        } else {
            console.log('[Background] 主监控页面未打开，跳过界面同步（页面打开后 loadCurrentRecipe 会初始化）');
        }
        
        console.log('[Background] 配方写入PLC：', row.RECIPE_NAME, '任务ID=', taskId, '批数=', batch);
    } catch (e) {
        console.error('[Background] PLC写入失败', e);
        callback();
        return;
    }

    // 2. 记录到数据库并删除队列
    jQuery.ajax({
        url: './php/AjaxService_CM.php?method=recordTransfer',
        data: {
            LOT_ID: row.LOT_ID,
            RECIPE_NAME: row.RECIPE_NAME,
            BATCH: row.BATCH,
            LOCAL_FLAG: row.LOCAL_FLAG,
            WEIGHT1_SET: row.WEIGHT1_SET,
            TOLERANCE1_SET: row.TOLERANCE1_SET,
            WEIGHT2_SET: row.WEIGHT2_SET,
            TOLERANCE2_SET: row.TOLERANCE2_SET,
            WEIGHT3_SET: row.WEIGHT3_SET,
            TOLERANCE3_SET: row.TOLERANCE3_SET
        },
        type: 'post',
        success: (text: string) => {
            const result = mini.decode(text);
            if (result.success) {
                // 3. 置位配方发送完成指令
                try {
                    (plc as any).DB401_DBW0_bit1 = 1;
                    console.log('[Background] 配方发送完成：', row.RECIPE_NAME);
                } catch (e) {
                    console.error('[Background] 发送完成指令置位失败', e);
                }
            } else {
                console.error('[Background] 记录发送历史失败：', result.message);
            }
            callback();
        },
        error: () => {
            console.error('[Background] 记录发送历史失败');
            callback();
        }
    });
}

// ========== 压力曲线记录 ==========

/**
 * 检测输送信号，启停压力曲线记录
 * @param sysNo 系统编号 1/2/3
 * @param value DB401_DBW0 当前值
 */
function checkCurveSignal(sysNo: number, value: number) {
    const idx = sysNo - 1;
    const cfg = curveTagMap[idx];
    const isOn = !!(value & (1 << cfg.bit));
    const recorder = curveRecorders[idx];

    if (isOn && !recorder.isRunning) {
        recorder.isRunning = true;
        startCurveReadTimer(sysNo);
        startCurveFlushTimer(sysNo);
        console.log(`[Background] ${sysNo}#压力曲线记录启动`);
    } else if (!isOn && recorder.isRunning) {
        stopCurveRecording(sysNo);
        console.log(`[Background] ${sysNo}#压力曲线记录停止，共记录 ${curveRecorders[idx].buffer.length} 条`);
    }
}

/**
 * 启动 2s 采样定时器：每 2 秒读一次 PLC 存入内存缓存
 */
function startCurveReadTimer(sysNo: number) {
    const idx = sysNo - 1;
    const cfg = curveTagMap[idx];

    curveRecorders[idx].readTimer = setInterval(() => {
        const batch = (plc as any)[cfg.batchField] || 0;
        const maxPressure = cfg.maxField ? ((plc as any)[cfg.maxField] || 0) : 0;
        const feedPressure = (plc as any)[cfg.feedField] || 0;
        const time = formatCurveTime(new Date());

        curveRecorders[idx].buffer.push({
            batch,
            maxPressure,
            feedPressure,
            time
        });
    }, 2000);
}

/**
 * 启动 20s 批量写入定时器：每 20 秒把缓存数据批量入库
 */
function startCurveFlushTimer(sysNo: number) {
    curveRecorders[sysNo - 1].flushTimer = setInterval(() => {
        flushCurveBuffer(sysNo);
    }, 20000);
}

/**
 * 停止压力曲线记录：清除定时器并 flush 剩余缓存
 */
function stopCurveRecording(sysNo: number) {
    const idx = sysNo - 1;
    const recorder = curveRecorders[idx];

    if (recorder.readTimer) {
        clearInterval(recorder.readTimer);
        recorder.readTimer = null;
    }
    if (recorder.flushTimer) {
        clearInterval(recorder.flushTimer);
        recorder.flushTimer = null;
    }

    // 最终 flush（即使缓存为空也调用，确保最后一批数据入库）
    flushCurveBuffer(sysNo);
    recorder.isRunning = false;
}

/**
 * 批量 flush 缓存数据到数据库
 */
function flushCurveBuffer(sysNo: number) {
    const idx = sysNo - 1;
    const recorder = curveRecorders[idx];
    if (recorder.buffer.length === 0) return;

    const payload = recorder.buffer.map(p => ({
        SYS_NO: sysNo,
        BATCH: p.batch,
        MAX_PRESSURE: p.maxPressure,
        FEED_PRESSURE: p.feedPressure,
        SAVE_TIME: p.time
    }));

    console.log(`[Background] ${sysNo}#压力曲线批量写入: ${payload.length} 条`);

    jQuery.ajax({
        url: './php/AjaxService_CM.php?method=recordCurve',
        data: { data: mini.encode(payload) },
        type: 'post',
        success: (text: string) => {
            const result = mini.decode(text);
            if (result.success) {
                console.log(`[Background] ${sysNo}#压力曲线写入成功: ${result.count || payload.length} 条`);
            } else {
                console.error(`[Background] ${sysNo}#压力曲线写入失败:`, result.message);
            }
        },
        error: (xhr: any, status: string, err: string) => {
            console.error(`[Background] ${sysNo}#压力曲线请求失败:`, status, err, xhr.responseText);
        }
    });

    // 清空缓存（无论后端成功失败，避免重复发送）
    recorder.buffer = [];
}
